<template>
  <div class="hr-page crawl-page">
    <header class="console-page-head">
      <div class="console-page-copy">
        <p class="console-kicker">데이터</p>
        <h2 class="console-title">크롤링 관리</h2>
      </div>
      <div class="console-head-side crawl-header-actions">
        <v-btn variant="flat" class="action-btn btn-save" @click="excelDialogOpen = true">
          <v-icon class="mr-1" size="18">mdi-microsoft-excel</v-icon>
          엑셀 다운로드
        </v-btn>
        <v-btn variant="flat" class="action-btn btn-download" @click="downloadPcCrawler">
          <v-icon class="mr-1" size="18">mdi-download</v-icon>
          PC 크롤러
        </v-btn>
        <v-btn variant="flat" class="action-btn btn-guide" @click="crawlerGuideDialog = true">
          <v-icon class="mr-1" size="18">mdi-help-circle-outline</v-icon>
          사용법
        </v-btn>
      </div>
    </header>

    <div class="hr-body">
    <div class="hr-main-layout">

      <!-- 기관 분포 현황 (사업구분 · 지역) -->
      <div class="section-card distribution-section">
        <div class="section-header">
          <div class="section-header-left">
            <v-icon size="20" class="mr-2">mdi-chart-tree</v-icon>
            기관 분포 현황
          </div>
          <span class="distribution-note">복수 사업 기관은 사업구분별 중복 집계</span>
        </div>
        <div class="section-body distribution-body">
          <LoadingOverlay :show="statsLoading" text="통계 불러오는 중..." />

          <!-- 요약 -->
          <div class="dist-summary-row">
            <div class="dist-summary-item">
              <span class="dist-summary-label">총 기관</span>
              <span class="dist-summary-value">{{ stats.summary?.total_count?.toLocaleString() || 0 }}</span>
            </div>
            <div class="dist-summary-item">
              <span class="dist-summary-label">시도</span>
              <span class="dist-summary-value">{{ stats.summary?.sido_count || 0 }}</span>
            </div>
            <div class="dist-summary-item">
              <span class="dist-summary-label">시군구</span>
              <span class="dist-summary-value">{{ stats.summary?.sigungu_count || 0 }}</span>
            </div>
          </div>

          <!-- 사업구분별 분포 (컴팩트) -->
          <div class="dist-biz-section dist-biz-section--compact">
            <div class="dist-subtitle dist-subtitle--compact">사업구분별 기관 수</div>
            <div class="dist-biz-compact-row">
              <button
                v-for="biz in stats.byBiz"
                :key="biz.biz_short"
                type="button"
                class="dist-biz-chip"
                :class="{ active: selectedBiz === biz.biz_short }"
                @click="toggleBizFilter(biz.biz_short)"
              >
                <span class="dist-biz-dot" :style="{ background: biz.biz_color }"></span>
                <span class="dist-biz-name">{{ biz.biz_short }}</span>
                <span class="dist-biz-count">{{ biz.count?.toLocaleString() || 0 }}</span>
              </button>
            </div>
            <div v-if="selectedBiz" class="dist-filter-hint">
              <v-icon size="14" class="mr-1">mdi-filter</v-icon>
              {{ selectedBiz }} 강조 중
              <v-btn variant="text" size="x-small" class="btn-filter-clear" @click="selectedBiz = ''">해제</v-btn>
            </div>
          </div>

          <!-- 지역 × 사업구분 매트릭스 -->
          <div class="dist-matrix-section">
            <div class="dist-matrix-head">
              <div class="dist-subtitle">지역별 · 사업구분별 분포</div>
              <span class="dist-matrix-hint">
                <v-icon size="14" class="mr-1">mdi-open-in-new</v-icon>
                시군구 클릭 → 해당 지역 기관 목록 (새 창 · 사업구분 전체)
              </span>
            </div>
            <div class="dist-matrix-wrap">
              <table class="dist-matrix-table">
                <colgroup>
                  <col class="col-w-region">
                  <col class="col-w-total">
                  <col v-for="biz in visibleBizTypes" :key="`col-${biz.short}`" class="col-w-biz">
                </colgroup>
                <thead>
                  <tr>
                    <th class="col-region sticky-col">지역</th>
                    <th class="col-total">합계</th>
                    <th
                      v-for="biz in visibleBizTypes"
                      :key="biz.short"
                      class="col-biz"
                      :class="{ highlighted: selectedBiz === biz.short }"
                    >
                      <span class="th-dot" :style="{ background: biz.color }"></span>
                      {{ biz.short }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="region in stats.byRegion" :key="region.sido_code">
                    <tr
                      class="dist-region-row"
                      :class="{ expanded: expandedSido === region.sido_code }"
                      @click="toggleSidoExpand(region.sido_code)"
                    >
                      <td class="col-region sticky-col region-sido">
                        <v-icon size="16" class="expand-icon">
                          {{ expandedSido === region.sido_code ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                        </v-icon>
                        <span class="region-label">{{ region.sido_name }}</span>
                      </td>
                      <td class="col-total num-cell">
                        <span class="cell-num">{{ region.total_count?.toLocaleString() }}</span>
                        <span class="cell-pct">{{ getSharePct(region.total_count, stats.summary?.total_count) }}</span>
                      </td>
                      <td
                        v-for="biz in visibleBizTypes"
                        :key="`${region.sido_code}-${biz.short}`"
                        class="col-biz num-cell"
                        :class="{ highlighted: selectedBiz === biz.short, dimmed: selectedBiz && selectedBiz !== biz.short, 'has-value': region.biz?.[biz.short] }"
                      >
                        <span class="cell-num">{{ formatBizCount(region.biz?.[biz.short]) }}</span>
                        <span v-if="region.biz?.[biz.short]" class="cell-pct">
                          {{ getSharePct(region.biz[biz.short], region.total_count) }}
                        </span>
                      </td>
                    </tr>
                    <tr
                      v-for="sg in getSigunguRows(region.sido_code)"
                      v-show="expandedSido === region.sido_code"
                      :key="`${region.sido_code}-${sg.sigungu_code}`"
                      class="dist-sigungu-row"
                      @click="openSigunguSearch(sg, region.sido_name)"
                    >
                      <td class="col-region sticky-col sigungu-name">
                        <v-icon size="14" class="mr-1 sigungu-icon">mdi-map-marker-outline</v-icon>
                        <span class="region-label">{{ sg.sigungu_name }}</span>
                      </td>
                      <td class="col-total num-cell">
                        <span class="cell-num">{{ sg.total_count?.toLocaleString() }}</span>
                        <span class="cell-pct">{{ getSharePct(sg.total_count, region.total_count) }}</span>
                      </td>
                      <td
                        v-for="biz in visibleBizTypes"
                        :key="`${sg.sigungu_code}-${biz.short}`"
                        class="col-biz num-cell"
                        :class="{ highlighted: selectedBiz === biz.short, dimmed: selectedBiz && selectedBiz !== biz.short, 'has-value': sg.biz?.[biz.short] }"
                      >
                        <span class="cell-num">{{ formatBizCount(sg.biz?.[biz.short]) }}</span>
                        <span v-if="sg.biz?.[biz.short]" class="cell-pct">
                          {{ getSharePct(sg.biz[biz.short], sg.total_count) }}
                        </span>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="!stats.byRegion?.length" class="dist-empty-row">
                    <td :colspan="visibleBizTypes.length + 2">수집된 기관 데이터가 없습니다.</td>
                  </tr>
                </tbody>
                <tfoot v-if="stats.byRegion?.length">
                  <tr class="dist-total-row">
                    <td class="col-region sticky-col">전국 합계</td>
                    <td class="col-total num-cell">
                      <span class="cell-num">{{ stats.summary?.total_count?.toLocaleString() || 0 }}</span>
                    </td>
                    <td
                      v-for="biz in visibleBizTypes"
                      :key="`total-${biz.short}`"
                      class="col-biz num-cell"
                      :class="{ highlighted: selectedBiz === biz.short, 'has-value': getBizTotal(biz.short) }"
                    >
                      <span class="cell-num">{{ formatBizCount(getBizTotal(biz.short)) }}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

      <CrawlExcelDownloadDialog v-model="excelDialogOpen" />

      <!-- PC 크롤러 사용법 모달 -->
      <v-dialog v-model="crawlerGuideDialog" max-width="720">
        <v-card class="pc-crawler-guide-modal">
          <div class="guide-header">
            <v-icon size="24" class="mr-2">mdi-nodejs</v-icon>
            <h3>PC 크롤러</h3>
            <v-spacer />
            <v-btn icon variant="text" @click="crawlerGuideDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          
          <div class="guide-body">
          <!-- 실행 명령 -->
          <div class="command-builder">
            <div class="builder-title">
              <v-icon size="20" class="mr-2">mdi-console</v-icon>
              실행 방법
            </div>
            <div class="builder-desc">
              zip을 풀고 <strong>실행.bat</strong> 을 더블클릭하면 됩니다. Node.js 설치는 필요 없습니다.
              브라우저 창 <strong>1개</strong>가 화면 상단에 열리고, 아래 창에 진행이 나옵니다.
              전국을 <strong>한 바퀴</strong> 돈 뒤 끝납니다. 최근 일주일 안에 받은 기관은 건너뛰고,
              중간에 꺼도 다음에 다시 실행하면 이어서 수집합니다. 브라우저 창은 닫지 마세요.
            </div>

            <div class="generated-command">
              <div class="command-label-row">
                <span class="label">
                  <v-icon size="16" class="mr-1">mdi-mouse</v-icon>
                  실행
                </span>
                <span class="target-region">전국 한 바퀴 · 최근 일주일은 건너뜀 · 이어서 수집</span>
              </div>
              <div class="command-box">
                <code>실행.bat</code>
                <v-btn
                  variant="flat"
                  size="small"
                  class="copy-btn"
                  @click="copyCommand('실행.bat')"
                >
                  <v-icon size="16" class="mr-1">mdi-content-copy</v-icon>
                  복사
                </v-btn>
              </div>
            </div>

            <div class="sido-all-desc" style="margin-top: 12px;">
              <v-icon size="14" class="mr-1">mdi-information-outline</v-icon>
              CMD 하단에 <code>[바퀴][I#][시도][시군구] N/M 저장 · 소요시간</code> 요약이 출력됩니다.
            </div>

            <div class="command-examples">
              <div class="examples-title">
                <v-icon size="16" class="mr-1">mdi-book-open-page-variant</v-icon>
                명령어
              </div>
              <div
                v-for="(ex, idx) in commandExamples"
                :key="idx"
                class="example-row"
              >
                <div class="example-main">
                  <code>{{ ex.cmd }}</code>
                  <v-btn
                    variant="text"
                    size="x-small"
                    class="example-copy"
                    @click="copyCommand(ex.cmd)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <div class="example-desc">{{ ex.desc }}</div>
              </div>
            </div>
          </div>
          
          <!-- 설치 가이드 (접히는 섹션) -->
          <div class="install-guide">
            <div class="guide-toggle" @click="showInstallGuide = !showInstallGuide">
              <v-icon size="18" class="mr-2">mdi-help-circle-outline</v-icon>
              처음 사용하시나요? 설치 가이드 보기
              <v-spacer />
              <v-icon size="20">{{ showInstallGuide ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="showInstallGuide" class="guide-steps">
                <div class="guide-step">
                  <div class="step-num">1</div>
                  <div class="step-info">
                    <div class="step-title">PC 크롤러 다운로드</div>
                    <div class="step-desc">위쪽 「PC 크롤러」 버튼으로 zip을 받습니다.</div>
                  </div>
                </div>
                <div class="guide-step">
                  <div class="step-num">2</div>
                  <div class="step-info">
                    <div class="step-title">압축 해제</div>
                    <div class="step-desc">원하는 폴더에 zip을 풉니다. Node.js는 따로 설치하지 않습니다.</div>
                  </div>
                </div>
                <div class="guide-step">
                  <div class="step-num">3</div>
                  <div class="step-info">
                    <div class="step-title">실행</div>
                    <div class="step-desc">
                      <div class="inline-command">
                        <code>실행.bat</code>
                        <button class="mini-copy" @click="copyCommand('실행.bat')">
                          <v-icon size="12">mdi-content-copy</v-icon>
                        </button>
                      </div>
                      더블클릭하면 수집이 시작됩니다. 창을 닫지 마세요.
                    </div>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </div>
          </div><!-- /guide-body -->
          
          <div class="guide-footer">
            <v-icon size="16" class="mr-1">mdi-information-outline</v-icon>
            크롤링 결과는 서버로 자동 전송되며, <code>output</code> 폴더에 JSON 백업도 저장됩니다.
          </div>
        </v-card>
      </v-dialog>

    </div><!-- /hr-main-layout -->
    </div><!-- /hr-body -->
  </div><!-- /hr-page -->
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { crawlApi } from '@/services/api';
import { useStore } from 'vuex';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import CrawlExcelDownloadDialog from '@/components/crawl/CrawlExcelDownloadDialog.vue';

const store = useStore();

// 상태
const stats = ref<any>({ summary: {}, byRegion: [], byBiz: [], bySigungu: [], bizTypes: [] });
const statsLoading = ref(false);
const selectedBiz = ref('');
const expandedSido = ref('');
const sidoOptions = ref<any[]>([]);

/** PC 크롤러 압축 파일 다운로드 */
const downloadPcCrawler = () => {
  const link = document.createElement('a');
  link.href = '/local-crawler.zip';
  link.download = 'local-crawler.zip';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// PC 크롤러 가이드 모달
const crawlerGuideDialog = ref(false);
const showInstallGuide = ref(false);

const commandExamples = [
  { cmd: '실행.bat', desc: '더블클릭. 전국 한 바퀴, 최근 일주일은 건너뛰고 이어서 수집' },
  { cmd: 'node crawl.js --loop', desc: '고급: 한 바퀴 후에도 계속 반복' },
];

const visibleBizTypes = computed(() => stats.value.bizTypes || []);

const sigunguBySido = computed(() => {
  const map: Record<string, any[]> = {};
  (stats.value.bySigungu || []).forEach((sg: any) => {
    if (!map[sg.sido_code]) map[sg.sido_code] = [];
    map[sg.sido_code].push(sg);
  });
  Object.values(map).forEach(arr => {
    arr.sort((a, b) => String(a.sigungu_name).localeCompare(String(b.sigungu_name), 'ko'));
  });
  return map;
});

const getSigunguRows = (sidoCode: string) => sigunguBySido.value[sidoCode] || [];

const getSharePct = (part: number | undefined, whole: number | undefined) => {
  if (!part || !whole) return '';
  return `${((part / whole) * 100).toFixed(1)}%`;
};

const formatBizCount = (count: number | undefined) => {
  if (!count) return '-';
  return count.toLocaleString();
};

const getBizTotal = (bizShort: string) => {
  const item = (stats.value.byBiz || []).find((b: any) => b.biz_short === bizShort);
  return item?.count || 0;
};

const toggleBizFilter = (bizShort: string) => {
  selectedBiz.value = selectedBiz.value === bizShort ? '' : bizShort;
};

const toggleSidoExpand = (sidoCode: string) => {
  expandedSido.value = expandedSido.value === sidoCode ? '' : sidoCode;
};

/** 시군구 클릭 → 시설검색 새 창 (사업구분 필터 없음 · 전체) */
const openSigunguSearch = (sg: any, sidoName: string) => {
  const params = new URLSearchParams();
  params.set('sido', String(sg.sido_code));
  params.set('sigungu', String(sg.sigungu_code));
  params.set('label', `${sidoName} ${sg.sigungu_name}`);

  const url = `/facility?${params.toString()}`;
  const width = 1200;
  const height = 820;
  const left = Math.max(0, Math.floor((window.screen.width - width) / 2));
  const top = Math.max(0, Math.floor((window.screen.height - height) / 2));
  const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes`;

  const win = window.open(url, 'facilitySearchByRegion', features);
  win?.focus();
};

const excelDialogOpen = ref(false);

// 데이터 조회
const fetchData = async () => {
  try {
    statsLoading.value = true;
    const [statsRes, sidoRes] = await Promise.all([
      crawlApi.getStats(),
      crawlApi.getSidoList()
    ]);

    if (statsRes.data.success) {
      stats.value = statsRes.data.data;
    }

    if (sidoRes.data.success) {
      sidoOptions.value = sidoRes.data.data;
    }
  } catch (error) {
    console.error('데이터 조회 오류:', error);
    store.commit('showSnackbar', { text: '통계 조회 실패', color: 'error' });
  } finally {
    statsLoading.value = false;
  }
};

// 명령어 복사
const copyCommand = async (command: string) => {
  try {
    await navigator.clipboard.writeText(command);
    store.commit('showSnackbar', { text: '명령어가 복사되었습니다.', color: 'success' });
  } catch (err) {
    store.commit('showSnackbar', { text: '복사 실패', color: 'error' });
  }
};

// 마운트
onMounted(async () => {
  await fetchData();
});
</script>
